/**
 * useDelayedLoad Hook
 * 通用的延迟加载 hook，用于优化首屏性能
 *
 * 使用场景：
 * - 延迟加载非关键功能（Feed、Survey 等）
 * - 延迟初始化第三方服务
 * - 任何需要在首屏加载完成后再执行的操作
 *
 * @example
 * ```tsx
 * // Feed 系统延迟加载
 * const feedEnabled = useDelayedLoad({
 *   enabled: !!user,
 *   delay: 2500,
 *   useIdleCallback: true,
 *   debugName: 'Feed'
 * });
 *
 * // Survey 延迟加载
 * const surveyEnabled = useDelayedLoad({
 *   delay: 3000,
 *   debugName: 'Survey'
 * });
 *
 * return (
 *   <>
 *     {feedEnabled && <FeedComponent />}
 *     {surveyEnabled && <SurveyComponent />}
 *   </>
 * );
 * ```
 */

import { useState, useEffect } from 'react';

interface UseDelayedLoadOptions {
  /**
   * 是否启用延迟加载
   * 如果为 false，则不会开始延迟计时
   * @default true
   */
  enabled?: boolean;

  /**
   * 延迟时间（毫秒）
   * @default 2000
   */
  delay?: number;

  /**
   * 是否使用 requestIdleCallback
   * 如果为 true，会在浏览器空闲时执行
   * 如果浏览器不支持，自动 fallback 到 setTimeout
   * @default false
   */
  useIdleCallback?: boolean;

  /**
   * 调试名称，用于 console.log
   * @default 'DelayedLoad'
   */
  debugName?: string;
}

/**
 * 延迟加载 hook
 *
 * @param options - 配置选项
 * @returns 是否应该加载（true = 可以加载，false = 还在等待）
 */
export function useDelayedLoad(options?: UseDelayedLoadOptions): boolean {
  const {
    enabled = true,
    delay = 2000,
    useIdleCallback = false,
    debugName = 'DelayedLoad',
  } = options || {};

  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // 如果未启用，不执行延迟加载
    if (!enabled) {
      return;
    }

    const enableLoad = () => {
      console.log(`[${debugName}] Delayed load enabled after ${delay}ms`);
      setShouldLoad(true);
    };

    // 使用 requestIdleCallback（如果启用且浏览器支持）
    if (useIdleCallback && 'requestIdleCallback' in window) {
      const idleCallbackId = requestIdleCallback(enableLoad, { timeout: delay });
      return () => cancelIdleCallback(idleCallbackId);
    }
    // 否则使用 setTimeout
    else {
      const timeoutId = setTimeout(enableLoad, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [enabled, delay, useIdleCallback, debugName]);

  return shouldLoad;
}

/**
 * 预设配置：Feed 系统延迟加载
 *
 * @example
 * ```tsx
 * const feedEnabled = useDelayedLoadForFeed(!!user);
 * ```
 */
export function useDelayedLoadForFeed(enabled: boolean): boolean {
  return useDelayedLoad({
    enabled,
    delay: 2500,
    useIdleCallback: true,
    debugName: 'Feed',
  });
}

/**
 * 预设配置：Survey 延迟加载
 *
 * @example
 * ```tsx
 * const surveyEnabled = useDelayedLoadForSurvey();
 * ```
 */
export function useDelayedLoadForSurvey(): boolean {
  return useDelayedLoad({
    delay: 3000,
    useIdleCallback: false,
    debugName: 'Survey',
  });
}

/**
 * 预设配置：Analytics 延迟加载
 *
 * @example
 * ```tsx
 * const analyticsEnabled = useDelayedLoadForAnalytics();
 * ```
 */
export function useDelayedLoadForAnalytics(): boolean {
  return useDelayedLoad({
    delay: 2000,
    useIdleCallback: true,
    debugName: 'Analytics',
  });
}

/**
 * 预设配置：Landing 页面非首屏内容延迟加载
 *
 * @example
 * ```tsx
 * const belowFoldEnabled = useDelayedLoadForLanding();
 * ```
 */
export function useDelayedLoadForLanding(): boolean {
  return useDelayedLoad({
    delay: 500,
    useIdleCallback: false,
    debugName: 'LandingBelowFold',
  });
}
